import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { Search, Plus, UserPlus } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import AuthenticatedLayout from '@/layouts/AuthenticatedLayout'
import PagyPagination from '@/components/PagyPagination'
import { capitalize } from '@/lib/utils'
import { useOrgPath } from '@/lib/pathFor'
import { formatTime } from '@/lib/formatters'
import ContactPromotionModal, { type ClientOption, type PromotionContact } from '@/components/clients/ContactPromotionModal'

interface ClientItem {
  id: string
  name: string
  status: string
  primary_contact_name: string | null
  primary_contact_email: string | null
  active_bookings_count: number
  email_threads_count: number
  credit_limit: number | null
  show_url: string
}

interface ClientsIndexProps {
  clients: ClientItem[]
  unknown_contacts?: UnknownContact[]
  client_options?: ClientOption[]
  view?: 'clients' | 'unknown_contacts'
  search: string
  pagy: { page: number; pages: number; count: number; next: number | null; prev: number | null }
}

interface UnknownContact {
  id: string
  name: string | null
  email: string
  company: string | null
  role: string | null
  last_email_date: string | null
  emails_count: number
}

export default function ClientsIndex({
  clients,
  unknown_contacts = [],
  client_options = [],
  view = 'clients',
  search: initialSearch,
  pagy,
}: ClientsIndexProps) {
  const [search, setSearch] = useState(initialSearch)
  const [promotionContact, setPromotionContact] = useState<PromotionContact | null>(null)
  const pathFor = useOrgPath()

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(pathFor('/clients'), { search, view }, { preserveState: true })
  }

  function switchView(nextView: 'clients' | 'unknown_contacts') {
    router.get(pathFor('/clients'), { view: nextView, search }, { preserveState: true })
  }

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <div className="flex items-center gap-5">
          <h1 className="text-sm font-semibold">Clients</h1>
          <div className="flex h-8 items-end gap-1">
            <button
              type="button"
              onClick={() => switchView('clients')}
              className={`h-8 px-2 text-[13px] cursor-pointer ${
                view === 'clients'
                  ? 'font-semibold text-primary border-b-2 border-primary'
                  : 'font-medium text-muted-foreground hover:text-foreground'
              }`}
            >
              Clients
            </button>
            <button
              type="button"
              onClick={() => switchView('unknown_contacts')}
              className={`h-8 px-2 text-[13px] cursor-pointer ${
                view === 'unknown_contacts'
                  ? 'font-semibold text-primary border-b-2 border-primary'
                  : 'font-medium text-muted-foreground hover:text-foreground'
              }`}
            >
              Unknown Contacts
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link href={pathFor('/clients/new')}>
            <Button size="sm" className="text-xs cursor-pointer">
              <Plus className="h-3.5 w-3.5 mr-1" />
              New Client
            </Button>
          </Link>
          <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-[200px] pl-8 text-[13px]"
            />
          </div>
          </form>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {view === 'unknown_contacts' ? (
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Contact</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Company</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Emails</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Last Email</th>
                <th className="px-6 py-2.5 text-right text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {unknown_contacts.map((contact) => (
                <tr key={contact.id} className="border-b hover:bg-muted/30">
                  <td className="px-6 py-3">
                    <div>
                      <span className="text-[13px] font-medium text-foreground">{contact.name || contact.email}</span>
                      <span className="ml-1.5 text-xs text-muted-foreground">{contact.email}</span>
                    </div>
                  </td>
                  <td className="px-6 py-3 text-[13px] text-muted-foreground">{contact.company || '-'}</td>
                  <td className="px-6 py-3 text-[13px] text-foreground">{contact.emails_count}</td>
                  <td className="px-6 py-3 text-[13px] text-muted-foreground">
                    {contact.last_email_date ? formatTime(contact.last_email_date) : '-'}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs cursor-pointer"
                      onClick={() => setPromotionContact(contact)}
                    >
                      <UserPlus className="mr-1.5 h-3 w-3" />
                      Convert
                    </Button>
                  </td>
                </tr>
              ))}
              {unknown_contacts.length === 0 && (
                <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-muted-foreground">No unknown contacts found</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/30 text-left">
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Name</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Primary Contact</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Active Bookings</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Threads</th>
                <th className="px-6 py-2.5 text-right text-xs font-medium text-muted-foreground">Credit limit</th>
                <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr
                  key={client.id}
                  className="border-b hover:bg-muted/30 cursor-pointer"
                  onClick={() => router.visit(client.show_url)}
                >
                  <td className="px-6 py-3">
                    <Link href={client.show_url} className="text-[13px] font-medium text-foreground hover:underline cursor-pointer">
                      {client.name}
                    </Link>
                  </td>
                  <td className="px-6 py-3">
                    {client.primary_contact_name ? (
                      <div>
                        <span className="text-[13px] text-foreground">{client.primary_contact_name}</span>
                        <span className="ml-1.5 text-xs text-muted-foreground">{client.primary_contact_email}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">No contacts</span>
                    )}
                  </td>
                  <td className="px-6 py-3 text-[13px] text-foreground">{client.active_bookings_count}</td>
                  <td className="px-6 py-3 text-[13px] text-foreground">{client.email_threads_count}</td>
                  <td className="px-6 py-3 text-right text-[13px] tabular-nums text-foreground">
                    {client.credit_limit != null ? `$${client.credit_limit.toLocaleString('en-US')}` : '—'}
                  </td>
                  <td className="px-6 py-3">
                    <Badge variant="secondary" size="sm" className={`font-medium ${client.status === 'active' ? 'bg-green-100 text-green-800' : client.status === 'prospect' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                      {capitalize(client.status)}
                    </Badge>
                  </td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">No clients found</td></tr>
              )}
            </tbody>
          </table>
        )}
        <PagyPagination pagy={pagy} />
      </div>

      <ContactPromotionModal
        key={promotionContact?.email || 'no-contact'}
        open={!!promotionContact}
        onClose={() => setPromotionContact(null)}
        contact={promotionContact}
        clients={client_options}
      />
    </>
  )
}

ClientsIndex.layout = (page: React.ReactNode) => (
  <AuthenticatedLayout>{page}</AuthenticatedLayout>
)
